import styled, { keyframes } from "styled-components";
import image from "../../../../../assets/img/image3.jpg"; // swap to relevant image
import { blueColor, goldColor } from "../../../../styled/Theme";
import {
  CreditCardOutlined,
  BellOutlined,
  LockOutlined,
  AuditOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
} from "@ant-design/icons";

const features = [
  {
    icon: <CreditCardOutlined />,
    title: "Digital Member Payments",
    body: "Seamless account interactions and digital payment acceptance for all member types.",
    color: blueColor,
  },
  {
    icon: <BellOutlined />,
    title: "Automated Activity Alerts",
    body: "Real-time notifications for savings milestones, loan disbursements, and account activity.",
    color: "#0ea5e9",
  },
  {
    icon: <LockOutlined />,
    title: "Institution-to-Member Messaging",
    body: "Secure, verified channels so every official communication reaches the right member.",
    color: goldColor,
  },
  {
    icon: <AuditOutlined />,
    title: "Integrated Audit Logs",
    body: "Communication and transaction records built for compliance, reporting, and regulatory review.",
    color: "#22c55e",
  },
];

const stats = [
  { value: "360°", label: "Member visibility" },
  { value: "< 2s", label: "Alert delivery" },
  { value: "100%", label: "Audit-ready records" },
];

const pillars = [
  { icon: <RiseOutlined />, label: "Growth-Oriented" },
  { icon: <SafetyCertificateOutlined />, label: "Regulator-Ready" },
  { icon: <TeamOutlined />, label: "Member-First" },
];

export default function SaccosInstitutions() {
  return (
    <Wrapper>
      <Inner className="container">
        {/* ── LEFT: Dark panel ── */}
        <LeftPanel
          data-aos="fade-right"
          data-aos-duration="800"
          data-aos-delay="100"
        >
          <PanelEyebrow>SACCOs &amp; Institutions</PanelEyebrow>

          <PanelHeadline>
            Digitised <GoldLine>Financial</GoldLine>
            <br />
            Operations &amp;
            <br />
            <GoldLine>Member</GoldLine> Engagement
          </PanelHeadline>

          <PanelSubline>
            One platform for payments, member communications, and full
            regulatory compliance — built for cooperatives and financial
            institutions at any scale.
          </PanelSubline>

          {/* Stat column */}
          <StatColumn>
            {stats.map((s, i) => (
              <StatItem
                key={i}
                data-aos="fade-up"
                data-aos-delay={300 + i * 100}
                data-aos-duration="600"
              >
                <StatValue>{s.value}</StatValue>
                <StatLabel>{s.label}</StatLabel>
                {i < stats.length - 1 && <StatDivider />}
              </StatItem>
            ))}
          </StatColumn>

          {/* Pillar chips */}
          <PillarRow>
            {pillars.map((p, i) => (
              <PillarChip
                key={i}
                data-aos="fade-up"
                data-aos-delay={500 + i * 80}
                data-aos-duration="500"
              >
                <PillarIcon>{p.icon}</PillarIcon>
                {p.label}
              </PillarChip>
            ))}
          </PillarRow>
        </LeftPanel>

        {/* ── RIGHT: Feature cards + image ── */}
        <RightCol>
          {/* Image strip */}
          <ImageStrip
            data-aos="fade-down"
            data-aos-duration="700"
            data-aos-delay="150"
          >
            <StripImage src={image} alt="SACCO digital operations" />
            <StripOverlay />
            <StripBadge>
              <BadgeDot />
              Compliant &amp; Documented
            </StripBadge>
          </ImageStrip>

          {/* Feature 2×2 grid */}
          <FeaturesGrid>
            {features.map((f, i) => (
              <FeatureCard
                key={i}
                $color={f.color}
                data-aos="fade-up"
                data-aos-delay={250 + i * 90}
                data-aos-duration="600"
              >
                <FeatureIconBar $color={f.color}>
                  <FeatureIcon $color={f.color}>{f.icon}</FeatureIcon>
                </FeatureIconBar>
                <FeatureBody>
                  <FeatureTitle>{f.title}</FeatureTitle>
                  <FeatureText>{f.body}</FeatureText>
                </FeatureBody>
                <FeatureCornerDot $color={f.color} />
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </RightCol>
      </Inner>
    </Wrapper>
  );
}

/* ── Animations ── */
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

/* ── Styles ── */

const Wrapper = styled.section`
  background: #ffffff;
  padding: 72px 0 80px;
`;

const Inner = styled.div`
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 52px;
  align-items: flex-start;
  padding: 0 20px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

/* ── Left Panel ── */
const LeftPanel = styled.div`
  background: linear-gradient(155deg, #071428 0%, #0d2254 60%, #091a3d 100%);
  border-radius: 24px;
  padding: 40px 36px;
  position: sticky;
  top: 100px;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -60px;
    right: -60px;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, ${blueColor}30 0%, transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -40px;
    left: -40px;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(circle, ${goldColor}20 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 980px) {
    position: static;
  }
`;

const PanelEyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${goldColor};
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
`;

const PanelHeadline = styled.h2`
  font-size: 32px;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: -1px;
  line-height: 1.15;
  margin: 0 0 16px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const GoldLine = styled.span`
  color: ${goldColor};
`;

const PanelSubline = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.7;
  margin: 0 0 32px;
  position: relative;
  z-index: 1;
`;

const StatColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 28px;
  position: relative;
  z-index: 1;
`;

const StatItem = styled.div`
  padding: 16px 0;
  position: relative;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: -1.5px;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 4px;
`;

const StatDivider = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
`;

const PillarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  position: relative;
  z-index: 1;
`;

const PillarChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 11.5px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s;
  cursor: default;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    border-color: ${goldColor}50;
  }
`;

const PillarIcon = styled.span`
  font-size: 12px;
  color: ${goldColor};
`;

/* ── Right Col ── */
const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ImageStrip = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  height: 200px;
  box-shadow: 0 12px 40px rgba(10, 30, 80, 0.12);
`;

const StripImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.7s ease;

  ${ImageStrip}:hover & {
    transform: scale(1.03);
  }
`;

const StripOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    rgba(7, 20, 55, 0.65) 0%,
    transparent 60%
  );
`;

const StripBadge = styled.div`
  position: absolute;
  bottom: 14px;
  left: 18px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
`;

const BadgeDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${goldColor};
  animation: ${pulse} 2s ease-in-out infinite;
`;

/* ── Feature grid ── */
const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div<{ $color: string }>`
  background: #f8fafc;
  border: 1px solid #e8f0f8;
  border-radius: 18px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.25s ease;
  cursor: default;

  &:hover {
    background: #ffffff;
    border-color: ${(p) => p.$color}40;
    box-shadow: 0 8px 28px ${(p) => p.$color}14;
    transform: translateY(-3px);
  }
`;

const FeatureIconBar = styled.div<{ $color: string }>`
  margin-bottom: 14px;
`;

const FeatureIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: ${(p) => p.$color}14;
  color: ${(p) => p.$color};
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.25s;

  ${FeatureCard}:hover & {
    background: ${(p) => p.$color}22;
  }
`;

const FeatureBody = styled.div``;

const FeatureTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
  line-height: 1.3;
`;

const FeatureText = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
`;

const FeatureCornerDot = styled.div<{ $color: string }>`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  opacity: 0.35;
`;
